import PropTypes from "prop-types";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  OrganisationContext,
  ReportsDownloadUpdate,
  UserContext,
} from "../../../layouts/MainLayout";

/**Third Part Libraries */
import Chart from "react-apexcharts";
import { Accordion } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import moment from "moment";

/**Custom Components**/
import EmptyScreen from "../../../utils/components/common/EmptyScreen/EmptyScreen";
import Icons from "../../../utils/components/icons";

/**Services and generic functions**/
import {
  cancelReportGeneration,
  generateGraphReport,
} from "../../../services/ReportService";
import { getModeValue } from "../../../utils/constants/deviceConstants";
import {
  backgroundAnotation,
  chartOptions,
  chartSeries,
} from "./deviceGraph/DeviceGraphOptions";
import { formatDate, takeScreenShot } from "../../../utils/utils";
import { backendConfig } from "../../../config/BackendConfig"
import "./deviceGraph/DeviceGraph.scss";
import CustomCheckbox from "../../../utils/components/common/CustomCheckbox/CustomCheckbox";

const checkShotSize = () => ["LABS", "STG"].includes(backendConfig.CURRENT_ENV);

const DeviceGraphReport = ({
  CustomToggle,
  setActiveAccordion,
  activeAccordion,
  selectedDevice = [],
  selectedDeviceDetails = [],
  durationErr,
  selectedDuration,
}) => {
  const { t } = useTranslation("reports");

  /**Context* section starts*/
  const { userCognitoData } = useContext(UserContext);
  const { siteData, orgData } = useContext(OrganisationContext);
  const { graphReportStatus, setGraphReportStatus } = useContext(
    ReportsDownloadUpdate
  );
  /**Context* section ends*/

  /**Checkbox states starts */
  const [isHumidityChecked, setIsHumidityChecked] = useState(true);
  const [isCIO2Checked, setIsCIO2Checked] = useState(true);
  const [isShotSizeChecked, setIsShotsizeChecked] = useState(checkShotSize);
  /**Checkbox states starts */

  const [loadingTime, setLoadingTime] = useState(0);

  /**graph data states starts */
  const [isGraphLoading, setIsGraphLoading] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [graphData, setGraphData] = useState([]);
  const [graphPlotted, setGraphPlotted] = useState(false);
  const [graphErr, setGraphErr] = useState(false);
  const [executionId, setExecutionId] = useState();
  const [reportJobId, setReportJobId] = useState();
  const [seriesData, setSeriesData] = useState([]);
  const [optionData, setOptionData] = useState({});
  /**graph data states ends */
  const screenShotRef = useRef();
  const [screenshotLoading, setScreenshotLoading] = useState(false);

  const rangeError = useMemo(() => {
    const diff = new Date(selectedDuration[1]) - new Date(selectedDuration[0]);
    const threeMonthsInMillis = 3 * 30 * 24 * 60 * 60 * 1000; // Assuming 30 days per month
    return diff > threeMonthsInMillis;
  }, [selectedDuration]);

  const showGraph = useMemo(() => {
    const status =
      !isGraphLoading &&
      graphPlotted &&
      !durationErr.isErr &&
      !rangeError &&
      selectedDevice?.length;
    return status;
  }, [isGraphLoading, graphPlotted, durationErr, rangeError, selectedDevice]);

  /**
   * This is a function that calclulates the time duration and displays date and time in Desired Custom Date Format.
   * Less than 1 day -  HH:MM AM/PM.
   * More than 1 day & less than 7 days (up to 7 days) - MM/DD HH:MM AM/PM.
   * More than 7 days- MM/DD.
   * Multiple Years - YYYY/MM/DD HH:MM AM/PM.
   * @param {array} timeStamp - the Selected Time Duration.
   * @returns {number} Desired Custom Date-Time Format.
   */
  const timeFormatter = useCallback(
    (value, currentTtimestamp) => {
      const msInDay = 24 * 60 * 60 * 1000;
      const msInWeek = 7 * msInDay;
      const date = new Date(currentTtimestamp);
      const startDate = new Date(selectedDuration[0]);
      const endDate = new Date(selectedDuration[1]);

      const durationDiff = endDate.getTime() - startDate.getTime();

      let isMultipleYear = false;

      if (endDate.getFullYear() !== startDate.getFullYear()) {
        // Scenario 4: Across multiple years
        isMultipleYear = true;
      }

      let optionsDate;

      if (
        durationDiff <= msInDay &&
        startDate.getDate() === endDate.getDate()
      ) {
        // Scenario 1: Less than or equal to 1 day and Should Not include Previous Day
        const optionsTime = {
          hour: "2-digit",
          minute: "2-digit",
        };

        const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

        return [formattedTime];
      } else if (durationDiff <= msInWeek) {
        // Scenario 2: Up to 7 days
        let formattedDate;
        if (isMultipleYear) {
          optionsDate = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          };
          formattedDate = date.toLocaleDateString("en-ZA", optionsDate);
        } else {
          optionsDate = {
            month: "2-digit",
            day: "2-digit",
          };
          formattedDate = date.toLocaleDateString("en-US", optionsDate);
        }

        const optionsTime = {
          hour: "2-digit",
          minute: "2-digit",
        };

        const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

        return [formattedDate, formattedTime];
      } else if (durationDiff > msInWeek) {
        // Scenario 3: More than 7 days
        let formattedDate;

        if (isMultipleYear) {
          optionsDate = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          };
          formattedDate = date.toLocaleDateString("en-ZA", optionsDate);
        } else {
          optionsDate = {
            month: "2-digit",
            day: "2-digit",
          };
          formattedDate = date.toLocaleDateString("en-US", optionsDate);
        }

        return [formattedDate];
      } else {
        const optionsDate = {
          day: "2-digit",
          month: "2-digit",
        };

        const optionsTime = {
          hour: "2-digit",
          minute: "2-digit",
        };
        const formattedDate = date.toLocaleDateString("en-ZA", optionsDate);
        const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

        return [formattedTime, formattedDate];
      }
    },
    [selectedDuration]
  );

  /**Calculate percentage of loading time for progress bar */
  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      const calculatedProgress = Math.min(
        (elapsedTime / (loadingTime * 1000)) * 100,
        100
      );
      setProgressPercentage(calculatedProgress);
      if (calculatedProgress >= 100) {
        clearInterval(interval);
      }
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [loadingTime]);

  /** Sequence-2 :  Call to generateGraphReport API to start report generation at cloud end*/
  const getDeviceGraphData = useCallback(async (queryParams) => {
    try {
      setGraphErr(false);
      setIsGraphLoading(true);
      setGraphData();
      setLoadingTime(0);
      const response = await generateGraphReport(queryParams);
      setExecutionId(response?.data?.executionId);
      setReportJobId(response?.data?.jobId);
      const approxTimeInSeconds =
        response?.data?.details?.approxTimeInSecondsForCompletion;
      setLoadingTime(
        approxTimeInSeconds ? Math.max(approxTimeInSeconds, 3) : 2
      );
    } catch (err) {
      setGraphData();
      setGraphErr(true);
      setLoadingTime(0);
    }
  }, []);

  /** Sequence-1: UseEffect to set query Param for generateGraphReport **/
  useEffect(() => {
    setGraphData();
    const isDataComplete =
      siteData.id &&
      userCognitoData?.username &&
      orgData?.TenantId &&
      selectedDevice &&
      selectedDevice.length &&
      !durationErr.isErr &&
      !rangeError;

    if (!isDataComplete) {
      return;
    }

    if (selectedDevice.length > 1) {
      setGraphData();
      return;
    }

    const queryParams = {
      requestedUserId: userCognitoData?.username,
      userId: userCognitoData?.username,
      tenantId: orgData?.TenantId,
      siteId: siteData.id,
      deviceId: selectedDevice[0],
      filters: {
        timeStamp: {
          between: selectedDuration,
        },
      },
      sort: [
        {
          timeStamp: "asc",
        },
      ],
    };

    getDeviceGraphData(queryParams);
  }, [
    getDeviceGraphData,
    orgData?.TenantId,
    selectedDevice,
    siteData.id,
    userCognitoData?.username,
    selectedDuration,
    durationErr,
    rangeError,
  ]);

  /** Sequence-3: UseEffect to fetch report data on getting MQTT event [graphReportStatus]
   *  and set data in  graphData state using setGraphData
   **/
  useEffect(() => {
    if (
      graphReportStatus &&
      executionId &&
      reportJobId === graphReportStatus?.reportJobId
    ) {
      const getDataFronS3Bucket = async () => {
        try {
          const fetchResponse = await fetch(graphReportStatus?.signedUrl);
          setGraphReportStatus(null);
          const response = await fetchResponse.json();
          setExecutionId();
          setGraphData(response);
          setIsGraphLoading(false);
          setLoadingTime(0);
        } catch (err) {
          setIsGraphLoading(false);
          console.log(err);
          setGraphData();
          setGraphErr(true);
        }
      };
      if (
        graphReportStatus?.reportStatus === "Completed" &&
        graphReportStatus?.signedUrl
      ) {
        getDataFronS3Bucket();
      }

      if (graphReportStatus?.reportStatus === "Failed") {
        setIsGraphLoading(false);
        setGraphErr(true);
      }
    }
  }, [graphReportStatus, setGraphReportStatus, reportJobId, executionId]);

  /** Sequence-5.1: After fetching report data & setting in graphData, update graph data [For online case] **/
  const getSeriesData = useCallback(
    (isHumidityChecked, isCO2Checked, isShotSizeChecked) => {
      const series = [...chartSeries];
      const options = { ...chartOptions };

      const modeChangeList = graphData?.modeEvent.map((modesData) => {
        return {
          ...backgroundAnotation[getModeValue(modesData.modes)],
          x: new Date(modesData.startTime).getTime(),
          x2: new Date(modesData.endTime).getTime(),
        };
      });

      series[0].data = isCO2Checked ? graphData?.telemetryData.clo2 : [];
      series[1].data = isHumidityChecked
        ? graphData?.telemetryData.humidity
        : [];
      series[2].data = isShotSizeChecked
        ? graphData?.telemetryData.lastShotSize
        : [];

      series[3].data = [
        [new Date(selectedDuration[0]), null],
        [new Date(selectedDuration[1]), null],
      ];

      if (
        !series[0].data.length &&
        !series[1].data.length &&
        !series[2].data.length
      ) {
        options.tooltip.enabled = false;
      }else {
        options.tooltip.enabled = true;
      }

      options.xaxis.labels.formatter = timeFormatter;
      options.annotations.xaxis = modeChangeList;
      options.yaxis[0].title = "";
      options.yaxis[1].title = "";
      options.yaxis[2].title = "";
      setSeriesData(series);
      setOptionData(options);
      setGraphPlotted(true);
    },
    [
      graphData?.modeEvent,
      graphData?.telemetryData,
      selectedDuration,
      timeFormatter,
    ]
  );
  /** Sequence-5.2: After fetching report data & setting in graphData, update graph data [For offline case] **/
  const getOfflineModeGraph = useCallback(() => {
    if (!selectedDevice?.length) return;
    const series = [...chartSeries];
    const options = { ...chartOptions };

    series[0].data = [];
    series[1].data = [];
    series[2].data = [];
    series[3].data = [
      [selectedDuration[0], null],
      [selectedDuration[1], null],
    ];

    options.xaxis.labels.formatter = timeFormatter;
    options.yaxis[0].title = "";
    options.yaxis[1].title = "";
    options.yaxis[2].title = "";
    options.annotations.xaxis = [
      {
        ...backgroundAnotation["NEWLY_ONBOARDED"],
        x: new Date(selectedDuration[0]).getTime(),
        x2: new Date(selectedDuration[1]).getTime(),
      },
    ];
    setSeriesData(series);
    setOptionData(options);
    setGraphPlotted(true);
  }, [selectedDuration, timeFormatter, selectedDevice?.length]);

  /** Sequence-4: After fetching report data or change in graphData,
   *  call the getSeriesData[for online case] or  getOfflineModeGraph[for offline case]
   **/
  useEffect(() => {
    setGraphPlotted(false);
    if (graphData?.telemetryData && !isGraphLoading) {
      setTimeout(() => {
        getSeriesData(isHumidityChecked, isCIO2Checked, isShotSizeChecked);
      }, [500]);
    }
    if (
      !isGraphLoading &&
      !graphData?.telemetryData &&
      !durationErr.isErr &&
      !rangeError
    ) {
      /**Case for offline mode */
      getOfflineModeGraph();
    }
  }, [
    graphData,
    isGraphLoading,
    durationErr.isErr,
    rangeError,
    isHumidityChecked,
    isCIO2Checked,
    rangeError,
    isShotSizeChecked,
  ]);

  /**Update err Msg **/
  const emptyMsg = useMemo(() => {
    if (durationErr.isErr) {
      return {
        title: t("no_data_to_display"),
        desc: durationErr.errMsg,
      };
    }
    if (rangeError) {
      return {
        title: t("no_data_to_display"),
        desc: t("rangeError", {
          maxRange: "3 months",
        }),
      };
    }
    if (!selectedDevice.length) {
      return {
        title: t("no_data_to_display"),
        desc: t("noDataMsg.deviceGraph"),
      };
    }
    if (graphErr) {
      return {
        title: t("no_data_to_display"),
        desc: t("downloadsFailed"),
      };
    }
    if (!showGraph) {
      return {
        loader: true,
        text: loadingTime ? `${Math.round(progressPercentage)}` : "",
        title: t("loading"),
        desc: t("graphLoading"),
        desc2: t("graph_note"),
      };
    }
    return {
      title: "",
      desc: "",
    };
  }, [
    durationErr.isErr,
    durationErr.errMsg,
    rangeError,
    selectedDevice.length,
    graphErr,
    showGraph,
    t,
    loadingTime,
    progressPercentage,
  ]);

  /** Sequence-12: API call to cancelReportGeneration to stop report generation at cloud end */
  const cancelCurrentJobForGraph = useCallback(async () => {
    if (executionId) {
      const param = {
        executionId: executionId,
      };
      setExecutionId();
      await cancelReportGeneration(param);
    }
  }, [executionId]);

  /** Sequence-11: Make call to function cancelCurrentJobForGraph on change in filter to stop report generation at cloud end */
  useEffect(() => {
    // cancelCurrentJobForGraph();
  }, [selectedDevice, cancelCurrentJobForGraph, selectedDuration]);

  const handleClickTakeScreenShot = useCallback(() => {
    setScreenshotLoading(true);
    const { cropPositionTop, cropPositionLeft, cropWidth, cropHeigth } =
      takeScreenShot(screenShotRef);

    html2canvas(screenShotRef.current, { scale: 1 }).then((canvas) => {
      let croppedCanvasDiv = document.createElement("canvas");
      let croppedCanvasContext2d = croppedCanvasDiv.getContext("2d");

      croppedCanvasDiv.width = cropWidth;
      croppedCanvasDiv.height = cropHeigth;

      croppedCanvasContext2d.drawImage(
        canvas,
        cropPositionLeft,
        cropPositionTop
      );
      const deviceDetail = selectedDeviceDetails[0];
      const timeZone = moment.tz(moment.tz.guess()).format("z");
      const row1text1 = "Device UDI : ";
      const row1text2 = `${deviceDetail?.deviceUUIDFromTelemetry || "-"}`;
      const row2text1 = "Duration : ";
      const row2text2 = `From ${formatDate(
        selectedDuration[0],
        false,
        false
      )} ${timeZone} to ${formatDate(
        selectedDuration[1],
        false,
        false
      )} ${timeZone}`;

      const row1text1Width =
        croppedCanvasContext2d.measureText(row1text1).width;
      const row2text1Width =
        croppedCanvasContext2d.measureText(row2text1).width;

      croppedCanvasContext2d.font = "20px Open Sans";
      croppedCanvasContext2d.fillText(row1text1, 80, 30);
      croppedCanvasContext2d.fillText(row2text1, 80, 70);

      croppedCanvasContext2d.font = "bold 20px Open Sans";
      croppedCanvasContext2d.fillText(row1text2, 140 + row1text1Width, 30);
      croppedCanvasContext2d.fillText(row2text2, 135 + row2text1Width, 70);

      const a = document.createElement("a");
      a.href = croppedCanvasDiv.toDataURL();

      const maxLength = 16;
      let trimmedDeviceName = deviceDetail?.DeviceName || "";
      if (trimmedDeviceName.length > maxLength) {
        trimmedDeviceName = trimmedDeviceName.substring(0, maxLength) + "...";
      }

      const fileName = `${trimmedDeviceName} - Graph.jpeg`;
      a.download = fileName;
      a.click();
      setScreenshotLoading(false);
    });
  }, [selectedDeviceDetails, selectedDuration]);

  return (
    <div className="graph-section">
      <Accordion
        activeKey={activeAccordion.graphViewActive ? ["graph-view"] : []}
      >
        <Card className="graph-view-container" ref={screenShotRef}>
          <Card.Header>
            <div
              className={`collapsed-header ${
                selectedDevice?.length > 1 ? "disable-graph" : ""
              }`}
            >
              <div
                className={`d-flex gap-2 align-item-center ${
                  activeAccordion.graphViewActive ? "hide" : "show"
                }`}
              >
                {Icons.graphIcon}
                <label>{t("deviceGraphHeading")}</label>
              </div>
              <div
                className={`${
                  activeAccordion.graphViewActive ? "show" : "hide"
                }`}
              >
                <div></div>
              </div>
              <div className="d-flex gap-4">
                <div
                  className={`d-flex gap-4 checkboxs ${
                    activeAccordion.graphViewActive ? "show" : "hide"
                  }`}
                >
                  <div className="checkBoxWrapper">
                    <CustomCheckbox
                      id="ppbCheckbox"
                      className="clo2-filter"
                      text={t("graphFilter.clo2")}
                      checked={isCIO2Checked}
                      onChange={(e) => {
                        if (showGraph) {
                          setIsCIO2Checked(e.target.checked);
                        }
                      }}
                      checkboxClassName="ppb"
                    />
                  </div>
                  <div className="checkBoxWrapper">
                    <CustomCheckbox
                      id="humidityCheckbox"
                      className="humidity-filter"
                      text={t("graphFilter.humidity")}
                      checked={isHumidityChecked}
                      onChange={(e) => {
                        if (showGraph) {
                          setIsHumidityChecked(e.target.checked);
                        }
                      }}
                      checkboxClassName="humidity"
                    />
                  </div>
                  <div className="checkBoxWrapper">
                    <CustomCheckbox
                      id="shotSizeCheckbox"
                      className="shotSize-filter"
                      text={t("graphFilter.shotSize")}
                      checked={isShotSizeChecked}
                      onChange={(e) => {
                        if (showGraph) {
                          setIsShotsizeChecked(e.target.checked);
                        }
                      }}
                      checkboxClassName="shotsize"
                    />
                  </div>
                  <div
                    className={`cameraIcon ${
                      showGraph
                        ? screenshotLoading
                          ? "disable"
                          : "enable"
                        : "disable"
                    }`}
                    title={t("screenshot")}
                    onClick={showGraph && handleClickTakeScreenShot}
                  >
                    {Icons.cameraIcon}
                    {screenshotLoading ? (
                      <div
                        className="spinner-border spinner-border-sm loader"
                        role="status"
                      ></div>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
                <CustomToggle
                  eventKey="graph-view"
                  customActiveAccordion={activeAccordion}
                  setCustomActiveAccordion={(val) => {
                    setActiveAccordion(val);
                  }}
                  selectedDevice={selectedDevice}
                >
                  {activeAccordion.graphViewActive
                    ? Icons.expandAccordion
                    : Icons.collapseAccordion}
                </CustomToggle>
              </div>
            </div>
          </Card.Header>
          <Accordion.Collapse eventKey="graph-view">
            <Card.Body>
              <span className="side-text side-text-1">
                {t("graphSideHeading.clo2")}
              </span>
              <div className={`data-container ${showGraph ? "showGraph" : ""}`}>
                {showGraph ? (
                  <div id="chart">
                    <Chart
                      options={optionData}
                      type="line"
                      series={seriesData}
                      width="100%"
                      height="100%"
                      className={"apex-device-chart"}
                    />
                  </div>
                ) : (
                  <EmptyScreen
                    img={Icons.emptyScreenReports}
                    {...emptyMsg}
                    small={true}
                  />
                )}
              </div>
              <span className="side-text side-text-2">
                {t("graphSideHeading.humidityAndShotsize")}
              </span>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </div>
  );
};

export default DeviceGraphReport;

DeviceGraphReport.propTypes = {
  selectedDevice: PropTypes.array,
  selectedDeviceDetails: PropTypes.array,
  CustomToggle: PropTypes.func,
  setActiveAccordion: PropTypes.func,
  activeAccordion: PropTypes.object,
  updateSelectedFilters: PropTypes.func,
  updateReportData: PropTypes.func,
  durationErr: PropTypes.object,
  selectedDuration: PropTypes.array,
};
